import { z } from 'zod';
import type { RouteSpecs } from '@rs/spec';
import {
	chainCodeHeader,
	chainSchema,
	currencySchema,
	errorResponseSchema,
	languageSchema,
	portfolioTransactionSchema,
	portfolioVaultSchema,
	referralCodeSchema,
	signatureSubmitSchema,
	stepSendTransactionSchema,
	stepSignMessageSchema,
	tierSchema,
	tokenAmountSchema,
	transactionInputSchema,
	unixSecondsSchema,
	vaultIdSchema,
	vaultSchema,
	walletBody,
	withdrawalSchema,
} from './schemas';

// Потолок периода графиков: ручки stats дополняют ряд до точки на каждый день диапазона,
// без ограничения запрос с from=0 генерировал бы десятки тысяч точек в ответе.
const MAX_PERIOD_DAYS = 400;
const SECONDS_PER_DAY = 86_400;

const periodRefine = (value: { from: number; to: number }, ctx: z.RefinementCtx): void => {
	if (value.from > value.to) {
		ctx.addIssue({ code: 'custom', message: 'from must be <= to', path: ['from'] });
	} else if (value.to - value.from > MAX_PERIOD_DAYS * SECONDS_PER_DAY) {
		ctx.addIssue({ code: 'custom', message: `period must be <= ${MAX_PERIOD_DAYS} days`, path: ['to'] });
	}
};

export const appRoutes = {
	// ============================================================
	// Общее
	// ============================================================

	getConfig: {
		method: 'POST',
		path: '/api/app/config',
		description: 'Конфигурация приложения: ссылки, валюты, языки, соцсети',
		tags: ['general'],
		response: {
			200: z.object({
				links: z.object({
					tos: z.string().describe('Terms of Service'),
					privacy: z.string().describe('Privacy Policy'),
					cookie: z.string().describe('Cookie Policy'),
					docs: z.string().describe('Документация'),
					risks: z.string().describe('Risk Disclosure'),
				}),
				supportEmail: z.string().describe('Email поддержки'),
				socialUrls: z.object({
					twitter: z.string(),
					discord: z.string(),
					telegram: z.string(),
				}),
				currencies: z.array(currencySchema).describe('Доступные валюты для отображения'),
				languages: z.array(languageSchema).describe('Доступные языки'),
			}),
			400: errorResponseSchema,
		},
	},

	listChains: {
		method: 'POST',
		path: '/api/app/chains/list',
		description: 'Список поддерживаемых EVM сетей',
		tags: ['general'],
		response: {
			200: z.object({
				chains: z.array(chainSchema),
			}),
			400: errorResponseSchema,
		},
	},

	getPrices: {
		method: 'POST',
		path: '/api/app/prices',
		description: 'Текущие цены токенов в USD и курсы фиатных валют',
		tags: ['general'],
		response: {
			200: z.object({
				tokens: z.record(z.string(), z.string()).describe('Тикер → цена в USD'),
				fiatRates: z.record(z.string(), z.string()).describe('ISO 4217 → курс к USD'),
			}),
			400: errorResponseSchema,
		},
	},

	// ============================================================
	// Vault'ы
	// ============================================================

	listVaults: {
		method: 'POST',
		path: '/api/app/vaults/list',
		description: 'Список vault с полными данными: описание, комиссии, аудиты, протоколы с аллокациями',
		tags: ['vaults'],
		headers: chainCodeHeader,
		response: {
			200: z.object({
				vaults: z.array(vaultSchema),
			}),
			400: errorResponseSchema,
		},
	},

	getVaultStats: {
		method: 'POST',
		path: '/api/app/vaults/stats',
		description: 'Исторические данные APY/TVL для графиков',
		tags: ['vaults'],
		headers: chainCodeHeader,
		body: z
			.object({
				vaultId: vaultIdSchema.describe('ID vault'),
				metric: z.enum(['apy', 'tvl']).describe('Метрика для графика'),
				from: unixSecondsSchema.describe('Unix seconds, начало периода'),
				to: unixSecondsSchema.describe('Unix seconds, конец периода'),
			})
			.superRefine(periodRefine),
		response: {
			200: z.object({
				summary: z.number().describe('APY — среднее за период (%), TVL — текущее значение (в токене)'),
				points: z.array(
					z.object({
						timestamp: z.number().describe('Unix seconds'),
						value: z.number().describe('APY — процент, TVL — сумма в токене'),
					}),
				),
			}),
			400: errorResponseSchema,
		},
	},

	// ============================================================
	// Пользователь
	// ============================================================

	getUserStatus: {
		method: 'POST',
		path: '/api/app/user/status',
		description: 'Статус пользователя: депозиты, реферальный код и ссылка',
		tags: ['user'],
		headers: chainCodeHeader,
		body: walletBody,
		response: {
			200: z.object({
				hasDeposited: z
					.boolean()
					.describe(
						'true если пользователь когда-либо депозитил (любая сеть). Флаг глобальный: фронт скрывает invite-code блок после первого депозита, чтобы повторный ввод реф-кода в новой сети не игнорировался индексером (referred_by фиксируется one-shot first-wins)',
					),
				deposits: z
					.record(z.string(), tokenAmountSchema)
					.describe(
						'vaultId → доступно к выводу (свободные shares × share_price, без зарезервированных заявками), только ненулевые в active vault',
					),
				referralCode: z.string().describe('Реферальный код пользователя, e.g. "A1B2C3"'),
				refLink: z
					.string()
					.describe('Полная реферальная ссылка для шаринга, e.g. "https://app.rstake.io/?ref=A1B2C3"'),
			}),
			400: errorResponseSchema,
		},
	},

	// ============================================================
	// Реферал
	// ============================================================

	validateReferral: {
		method: 'POST',
		path: '/api/app/referral/validate',
		description: 'Валидация реферального кода',
		tags: ['referral'],
		body: z.object({
			code: referralCodeSchema.describe('Реферальный код, 6 символов [A-Z0-9]'),
		}),
		response: {
			200: z.object({
				isValid: z.boolean().describe('true — код валиден'),
			}),
			400: errorResponseSchema,
		},
	},

	// ============================================================
	// Подпись сообщений
	// ============================================================

	submitSignature: {
		method: 'POST',
		path: '/api/app/signature/submit',
		description: 'Сохранение подписи после sign_message шага. Роутинг по purpose',
		tags: ['signature'],
		body: signatureSubmitSchema,
		response: {
			200: z.object({
				success: z.boolean().describe('true — подпись верифицирована и сохранена'),
			}),
			400: errorResponseSchema,
		},
	},

	// ============================================================
	// Транзакции
	// ============================================================

	buildTransactionSteps: {
		method: 'POST',
		path: '/api/app/transactions/build-steps',
		description: 'Построение шагов без создания бандла, для оценки газа через кошелёк',
		tags: ['transactions'],
		headers: chainCodeHeader,
		body: transactionInputSchema,
		response: {
			200: z.object({
				steps: z.array(z.union([stepSignMessageSchema, stepSendTransactionSchema])),
			}),
			400: errorResponseSchema,
		},
	},

	// ============================================================
	// Портфолио
	// ============================================================

	getPortfolioStatsTotal: {
		method: 'POST',
		path: '/api/app/portfolio/stats/total',
		description: 'Сводная статистика портфолио для верхних плашек',
		tags: ['portfolio'],
		headers: chainCodeHeader,
		body: walletBody,
		response: {
			200: z.object({
				deposited: z.string().describe('Сумма депозитов в USD'),
				depositedChange30d: z
					.number()
					.nullable()
					.describe('Изменение суммы депозитов в USD за 30 дней (процент). null если нет snapshot в окне'),
				earnedRewardsTotal: z.string().describe('Награды за всё время в USD'),
				earnedRewards30d: z
					.string()
					.nullable()
					.describe('Награды за 30 дней в USD. null если нет snapshot в окне [today-30d, today)'),
				apy: z.number().describe('Средневзвешенный APY по активным vault, процент'),
			}),
			400: errorResponseSchema,
		},
	},

	getPortfolioStatsDetails: {
		method: 'POST',
		path: '/api/app/portfolio/stats/details',
		description: 'Данные для графика Portfolio performance',
		tags: ['portfolio'],
		headers: chainCodeHeader,
		body: walletBody
			.extend({
				vaultId: vaultIdSchema.optional().describe('ID vault или не передан = все vault'),
				metric: z.enum(['deposited', 'rewards', 'apy']).describe('Метрика для графика'),
				from: unixSecondsSchema.describe('Unix seconds, начало периода'),
				to: unixSecondsSchema.describe('Unix seconds, конец периода'),
				currency: z
					.enum(['token', 'usd'])
					.optional()
					.describe('Отображение в токене или USD, по умолчанию usd. token допустим только с vaultId'),
			})
			.superRefine((value, ctx) => {
				periodRefine(value, ctx);
				if (value.currency === 'token' && !value.vaultId) {
					ctx.addIssue({ code: 'custom', message: 'currency=token requires vaultId', path: ['currency'] });
				}
			}),
		response: {
			200: z.object({
				summary: z.object({
					value: z.number().describe('Сводное значение за период (без форматирования — формат на фронте)'),
					changePercent: z
						.number()
						.optional()
						.describe('Изменение в %, только для metric=deposited и только когда есть база на начало периода'),
				}),
				points: z.array(
					z.object({
						timestamp: z.number().describe('Unix seconds'),
						value: z.number().describe('Значение метрики'),
					}),
				),
			}),
			400: errorResponseSchema,
		},
	},

	listPortfolioWithdrawals: {
		method: 'POST',
		path: '/api/app/portfolio/withdrawals/list',
		description: 'Список заявок на вывод, отсортированных по близости к готовности',
		tags: ['portfolio'],
		headers: chainCodeHeader,
		body: walletBody,
		response: {
			200: z.object({
				withdrawals: z.array(withdrawalSchema),
			}),
			400: errorResponseSchema,
		},
	},

	listPortfolioVaults: {
		method: 'POST',
		path: '/api/app/portfolio/vaults/list',
		description: 'Список депозитов пользователя + vault в других сетях',
		tags: ['portfolio'],
		headers: chainCodeHeader,
		body: walletBody,
		response: {
			200: z.object({
				vaults: z.array(portfolioVaultSchema),
				historicalVaults: z
					.array(
						z.object({
							id: z.string().describe('ID vault'),
							name: z.string().describe('Название vault'),
							tokenSymbol: z.string().describe('Тикер токена'),
							tokenIconUrl: z.string().describe('URL иконки токена'),
						}),
					)
					.describe('Vault с нулевым балансом, где ранее были средства'),
				otherChains: z
					.array(
						z.object({
							chainCode: z.string().describe('Символьный код сети, e.g. "base"'),
							chainName: z.string().describe('Отображаемое название сети'),
							chainIconUrl: z.string().describe('URL иконки сети'),
							vaultCount: z.number().describe('Количество vault с депозитами'),
							totalUsd: z.string().describe('Суммарный объём в USD'),
						}),
					)
					.describe('Vault в других сетях'),
			}),
			400: errorResponseSchema,
		},
	},

	getPortfolioVaultDetails: {
		method: 'POST',
		path: '/api/app/portfolio/vaults/details',
		description: 'История операций по конкретному депозиту',
		tags: ['portfolio'],
		headers: chainCodeHeader,
		body: walletBody.extend({
			vaultId: vaultIdSchema.describe('ID vault'),
		}),
		response: {
			200: z.object({
				transactions: z.array(portfolioTransactionSchema),
			}),
			400: errorResponseSchema,
		},
	},

	// ============================================================
	// Профиль
	// ============================================================

	getProfileDetails: {
		method: 'POST',
		path: '/api/app/profile/details',
		description: 'Профиль пользователя: поинты, тир, реферальная система',
		tags: ['profile'],
		body: walletBody,
		response: {
			200: z.object({
				points: z.number().describe('Текущее количество поинтов (XPRS)'),
				totalDeposited: z.string().describe('Сумма текущих депозитов в USD'),
				tier: tierSchema.describe('Текущий тир'),
				nextTier: z
					.object({
						name: z.string().describe('Название следующего тира'),
						pointsRequired: z.number().describe('Сколько поинтов нужно для следующего тира'),
						pointsRemaining: z.number().describe('Сколько осталось набрать'),
					})
					.optional()
					.describe('Отсутствует если текущий тир максимальный'),
				dailyPoints: z.object({
					total: z.number().describe('Общее начисление в день'),
					fromOwnDeposits: z.number().describe('За свои депозиты'),
					fromReferralDeposits: z.number().describe('За депозиты рефералов'),
					tierMultiplier: z.number().describe('Множитель от тира'),
					referrerBonus: z.number().describe('Аддитивный бонус XP/$1 за то что пришёл по рефералке, 0 если нет'),
				}),
				referral: z.object({
					code: z.string().describe('Реферальный код'),
					link: z.string().describe('Полная ссылка с реферальным кодом'),
					count: z.number().describe('Количество рефералов'),
					deposits: z.string().describe('Сумма депозитов рефералов в USD'),
				}),
				referralRules: z.object({
					pointsPerDollarOwn: z.number().describe('XP за $1 своего депозита в день'),
					pointsPerDollarReferral: z.number().describe('XP за $1 депозита реферала в день'),
					referrerBonusPerDollar: z
						.number()
						.describe('Аддитивный бонус XP/$1 своего депозита если пришёл по рефералке'),
					tiers: z.array(
						z.object({
							name: z.string().describe('Название тира'),
							minPoints: z.number().describe('Порог поинтов для входа'),
							multiplier: z.number().describe('Множитель начислений'),
						}),
					),
				}),
			}),
			400: errorResponseSchema,
		},
	},
} as const satisfies RouteSpecs;

export type AppRoutes = typeof appRoutes;
